import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Leaf, Facebook, Instagram, Twitter } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    'Lawn Installation & Maintenance',
    'Landscape Design',
    'Patio & Hardscape Installation',
    'Irrigation Systems',
    'Seasonal Yard Cleanup',
    'Tree & Shrub Trimming',
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms & Conditions', path: '/terms-conditions' },
    { name: 'Refund & Cancellation', path: '/refund-policy' },
    { name: 'Disclaimer', path: '/disclaimer' },
    { name: 'Cookie Policy', path: '/cookie-policy' },
  ];

  return (
    <footer className="bg-[#1A1A1A] text-white" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-[#2E5C31] rounded-full flex items-center justify-center">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold font-['Poppins']">DecorVista</span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Professional landscaping services in Seattle, WA. Transform your outdoor spaces with our experienced team.
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 bg-[#2E5C31] rounded-full flex items-center justify-center hover:bg-[#244A26] transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-[#2E5C31] rounded-full flex items-center justify-center hover:bg-[#244A26] transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-[#2E5C31] rounded-full flex items-center justify-center hover:bg-[#244A26] transition-colors" aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 font-['Poppins']">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-gray-400 hover:text-[#D4E157] transition-colors text-sm"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 font-['Poppins']">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-[#D4E157] transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 font-['Poppins']">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#2E5C31] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  667 Harbor View Drive<br />
                  Seattle, WA 98101
                </span>
              </li>
              <li>
                <a href="tel:+18888808889" className="flex items-center gap-3 text-gray-400 hover:text-[#D4E157] transition-colors" data-testid="footer-phone">
                  <Phone className="w-5 h-5 text-[#2E5C31]" />
                  <span className="text-sm">+1-888-880-8889</span>
                </a>
              </li>
              <li>
                <a href="mailto:support@decorvista.store" className="flex items-center gap-3 text-gray-400 hover:text-[#D4E157] transition-colors" data-testid="footer-email">
                  <Mail className="w-5 h-5 text-[#2E5C31]" />
                  <span className="text-sm">support@decorvista.store</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#2E5C31] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  Mon - Sat: 9:00 AM - 6:00 PM<br />
                  Sunday: Closed
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Links */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 mb-6">
            {legalLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-gray-500 hover:text-[#D4E157] transition-colors text-sm"
                data-testid={`footer-legal-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>
          
          <div className="text-center">
            <p className="text-gray-500 text-sm mb-2">
              DecorVista Landscaping Services | 667 Harbor View Drive, Seattle, WA 98101
            </p>
            <p className="text-gray-600 text-xs">
              Service pricing varies depending on project scope, materials, and property size.
            </p>
            <p className="text-gray-600 text-xs mt-4">
              &copy; {currentYear} DecorVista. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
